import React, {
  createContext,
  useContext,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
  RefObject
} from 'react';
import { connect, WindowMessenger } from 'penpal';
import ServiceApi from 'vl-common/src/services/service.api';
import { ManageAppointment } from './RoomCacheManager';
import { clearPreviouslyPromisedRooms } from '@hooks/useTestPatientRoom';
import { AutomatedClinicianIframe } from '@components/AutomatedClinicianIframe';
import type {
  ChildAutomatedParticipantAPI,
  BearerToken,
  TwilioTestToken,
  ParticipantLogEntry
} from '../../lib/automatedParticipant/types';
import { Room } from 'twilio-video';
import { CallManager } from '@helpers/CallManager';

type ConnectionState = 'idle' | 'connecting' | 'ready' | 'active' | 'error';

interface TestCallContextType {
  isTestCallEnabled: boolean;
  setIsTestCallEnabled: (val: boolean) => void;
  hasAcceptedTestCall: boolean;
  setHasAcceptedTestCall: (val: boolean) => void;
  connectionState: ConnectionState;
  startSession: () => Promise<void>;
  stopSession: () => Promise<void>;
  iframeRef: RefObject<HTMLIFrameElement | null>;
  room?: Room;
  setRoom: (room?: Room) => void;
  signalingManager?: CallManager;
  setSignalingManager: (manager?: CallManager) => void;
}

const TestCallContext = createContext<TestCallContextType | undefined>(undefined);

export const PatientTestCallManager = ({ children }: { children: ReactNode }) => {
  const [isTestCallEnabled, setIsTestCallEnabled] = useState(false);
  const [hasAcceptedTestCall, setHasAcceptedTestCall] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [room, setRoom] = useState<Room | undefined>(undefined);
  const [signalingManager, setSignalingManager] = useState<CallManager | undefined>(undefined);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const connectionRef = useRef<ReturnType<typeof connect<ChildAutomatedParticipantAPI>> | null>(null);
  const childApiRef = useRef<ChildAutomatedParticipantAPI | null>(null);
  const isDestroyedRef = useRef(false);

  // Reset state when test call is disabled
  useEffect(() => {
    if (!isTestCallEnabled) {
      clearPreviouslyPromisedRooms();
      setRoom(undefined);
      setSignalingManager(undefined);
      setConnectionState('idle');
      setHasAcceptedTestCall(false);
    }
  }, [isTestCallEnabled, setRoom, setSignalingManager]);

  // Setup Penpal when enabled and iframe is mounted
  useEffect(() => {
    if (!isTestCallEnabled) return;

    // Reset destroyed flag when re-enabling
    isDestroyedRef.current = false;

    const checkIframe = setInterval(() => {
      console.log('Checking for iframe window...', iframeRef.current?.contentWindow);
      if (iframeRef.current?.contentWindow) {
        console.log('Iframe window found! Starting setupPenpal...');
        clearInterval(checkIframe);
        setupPenpal();
      }
    }, 500);

    const setupPenpal = async () => {
      const iframe = iframeRef.current;
      if (!iframe?.contentWindow) return;

      setConnectionState('connecting');

      try {
        const connection = connect<ChildAutomatedParticipantAPI>({
          messenger: new WindowMessenger({
            remoteWindow: iframe.contentWindow,
            allowedOrigins: [globalThis.window.location.origin]
          }),
          methods: {
            getVlBearerToken: async (): Promise<BearerToken> => {
              const { Auth } = await import('aws-amplify');
              const session = await Auth.currentSession();
              const idToken = session.getIdToken();
              const token = idToken.getJwtToken();
              const expiration = 'getExpiration' in idToken ? idToken.getExpiration() * 1000 : Date.now() + 3600000;
              return {
                token: `Bearer ${token}`,
                expiresAt: expiration
              };
            },
            getTwilioTestToken: async (): Promise<TwilioTestToken> => {
              const guid = ServiceApi.userGuid;
              if (!guid) {
                throw new Error('User guid not available');
              }
              const tokenResponse = await ServiceApi.get('/videotokengen', {
                params: { appointment_id: undefined, user_guid: guid, automated: true }
              });
              const token = tokenResponse?.data?.token;
              const roomName = tokenResponse?.data?.roomName;
              if (!token || !roomName) {
                throw new Error('Missing video token or room name');
              }
              return {
                token,
                roomName,
                expiresAt: Date.now() + 3600000
              };
            },
            logEvent: async (entry: ParticipantLogEntry): Promise<void> => {
              console.log(`[AutoClinician] ${entry.level}: ${entry.message}`, entry.context);
            },
            getParticipantConfig: async () => undefined
          }
        });

        connectionRef.current = connection;
        const childApi = await connection.promise;
        childApiRef.current = childApi;
        setConnectionState('ready');
      } catch (error) {
        console.error('[PatientTestCallManager] Penpal setup failed:', error);
        setConnectionState('error');
      }
    };

    return () => {
      clearInterval(checkIframe);
      isDestroyedRef.current = true;
      if (connectionRef.current) {
        connectionRef.current.destroy();
        connectionRef.current = null;
      }
      childApiRef.current = null;
    };
  }, [isTestCallEnabled]);

  const startSession = useCallback(async () => {
    if (!childApiRef.current || isDestroyedRef.current) return;
    try {
      setConnectionState('active');
      childApiRef.current.startSession().catch((err) => {
        if (!isDestroyedRef.current) {
          console.error('[PatientTestCallManager] startSession error:', err);
        }
      });
    } catch (error) {
      console.error('[PatientTestCallManager] Failed to start session:', error);
      setConnectionState('error');
    }
  }, []);

  const stopSession = useCallback(async () => {
    if (!childApiRef.current || isDestroyedRef.current) return;
    try {
      await childApiRef.current.stopSession();
      setConnectionState('ready');
    } catch (error) {
      if (!isDestroyedRef.current) {
        console.error('[PatientTestCallManager] Failed to stop session:', error);
      }
    }
  }, []);

  // Auto-start when ready
  useEffect(() => {
    if (connectionState === 'ready') {
      startSession();
    }
  }, [connectionState, startSession]);

  const isConnectingOrReady = ['connecting', 'ready', 'active'].includes(connectionState);

  const contextValue = useMemo(
    () => ({
      isTestCallEnabled,
      setIsTestCallEnabled,
      hasAcceptedTestCall,
      setHasAcceptedTestCall,
      connectionState,
      startSession,
      stopSession,
      iframeRef,
      room,
      setRoom,
      signalingManager,
      setSignalingManager
    }),
    [
      isTestCallEnabled,
      setIsTestCallEnabled,
      hasAcceptedTestCall,
      setHasAcceptedTestCall,
      connectionState,
      startSession,
      stopSession,
      iframeRef,
      room,
      setRoom,
      signalingManager,
      setSignalingManager
    ]
  );

  return (
    <TestCallContext.Provider value={contextValue}>
      {children}

      {isTestCallEnabled && (
        <AutomatedClinicianIframe
          ref={iframeRef}
          style={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none' }}
        />
      )}

      {isConnectingOrReady && <ManageAppointment />}
    </TestCallContext.Provider>
  );
};

export const useTestCall = () => {
  const context = useContext(TestCallContext);
  if (!context) {
    throw new Error('useTestCall must be used within a PatientTestCallManager');
  }
  return context;
};
